import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, mangaList } from '@/mocks/mangaData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || 'all';
  const [activeTab, setActiveTab] = useState(activeSlug);

  useEffect(() => {
    setActiveTab(activeSlug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  const filteredManga = useMemo(() => {
    if (activeTab === 'all') return mangaList;
    return mangaList.filter((m) => m.category === activeTab);
  }, [activeTab]);

  const currentCategory = categories.find((c) => c.slug === activeTab);

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '全部'} - h漫画分类`,
    description: currentCategory?.description || '浏览所有漫画分类',
    url: `https://hmanga.com/category/${activeTab}`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'h漫画',
      url: 'https://hmanga.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }} />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: currentCategory?.name || '全部' },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading mb-2">
              {currentCategory?.name || '全部漫画'}
            </h1>
            <p className="text-sm text-foreground-500 mb-6">{currentCategory?.description || '浏览所有漫画作品'}</p>

            <div className="flex flex-wrap items-center gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveTab(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-400 hover:text-foreground-200 hover:bg-background-200 border border-background-300/60'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredManga.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-foreground-500 text-lg">暂无该分类的漫画作品</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
                {filteredManga.map((manga) => (
                  <Link
                    key={manga.id}
                    to={`/article/${manga.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                      <img
                        src={manga.cover}
                        alt={manga.title}
                        title={`${manga.title} - ${currentCategory?.name || 'h漫画'}`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                        <span className="text-xs text-foreground-100 bg-primary-500 px-3 py-1.5 rounded whitespace-nowrap">立即阅读</span>
                      </div>
                      <div className="absolute top-2 left-2">
                        <span className="bg-accent-500 text-background-950 text-xs font-bold px-2 py-0.5 rounded whitespace-nowrap">
                          {manga.rating}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-200 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                      {manga.title}
                    </h3>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-foreground-600">{manga.author}</span>
                      <span className="text-xs text-foreground-600">{(manga.views / 10000).toFixed(1)}万</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}